import { useState } from "react";
import { Paper, Stack, Typography, Chip, Button, IconButton, FormControl, FormLabel, TextField, InputAdornment, Tooltip, LinearProgress, Box, Grid } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { format, parseISO } from "date-fns";

const formatDate = (dateStr) => {
  if (!dateStr || dateStr === "9999-01-01T00:00:00.000Z") return "N/A";
  try {
    return format(parseISO(dateStr), "dd MMM, yyyy");
  } catch (err) {
    return "N/A";
  }
};

const LinkedinDealItem = ({ deal, isCompanyManaged, anyBusy, onEdit, onDelete, onRecover }) => {
  const [copied, setCopied] = useState(false);
  const feedUrl = deal.feedUrl;

  const handleCopyFeedUrl = () => {
    if (feedUrl) {
      navigator.clipboard.writeText(feedUrl).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  // Determine status color and styling
  const isActive = deal.status === "ACTIVE";
  const isSuspended = deal.status === "SUSPENDED";

  let statusColor = "default";
  let statusBorderColor = "#e0e0e0";

  if (isActive) {
    statusColor = "success";
    statusBorderColor = "#2e7d32"; // Success green
  } else if (isSuspended) {
    statusColor = "error";
    statusBorderColor = "#d32f2f"; // Error red
  }

  // Calculate slots percentage
  const totalSlots = deal.totalSlots || 0;
  const usedSlots = deal.usedSlots || 0;
  const slotsPercentage = totalSlots > 0 ? Math.min(100, Math.max(0, (usedSlots / totalSlots) * 100)) : 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        position: "relative",
        borderRadius: 2,
        borderLeft: `5px solid ${statusBorderColor}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      }}
    >
      {/* Header section */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2" fontWeight={700} color="text.primary">
            Deal #{deal.id}
          </Typography>
          <Chip label={deal.status} size="small" color={statusColor} variant="contained" sx={{ fontWeight: 600, fontSize: "0.7rem", height: 20 }} />
        </Stack>

        <Stack direction="row" spacing={0.5} alignItems="center">
          {isSuspended && (
            <Button
              size="small"
              variant="contained"
              color="warning"
              onClick={() => onRecover(deal.id)}
              disabled={anyBusy}
              sx={{
                py: 0.25,
                px: 1,
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: 1,
                mr: 0.5,
              }}
            >
              Recover
            </Button>
          )}
          <Tooltip title="Edit Deal">
            <IconButton color="primary" size="small" onClick={() => onEdit(deal)} disabled={anyBusy}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Deal">
            <IconButton color="error" size="small" onClick={() => onDelete(deal.id)} disabled={anyBusy}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Stack spacing={2}>
        {/* Slots Usage Progress bar */}
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
            <Typography variant="body2" color="text.secondary">
              Slot Allocation:{" "}
              <strong>
                {usedSlots} / {totalSlots} used
              </strong>
            </Typography>
            <Typography variant="body2" fontWeight={700} color={slotsPercentage >= 100 ? "error.main" : "primary.main"}>
              {Math.round(slotsPercentage)}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={slotsPercentage}
            color={slotsPercentage >= 100 ? "error" : slotsPercentage >= 80 ? "warning" : "primary"}
            sx={{ height: 6, borderRadius: 3, bgcolor: "action.hover" }}
          />
        </Box>

        {/* Details Grid (Not Company Managed / Admin Managed deals) */}
        {!isCompanyManaged && (
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                Created At
              </Typography>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                {formatDate(deal.createdAt)}
              </Typography>
            </Grid>

            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                Monthly Amount
              </Typography>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                {deal.monthlyCreditAmount != null ? `$${deal.monthlyCreditAmount}` : "N/A"}
              </Typography>
            </Grid>

            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                Expiry Date
              </Typography>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                {formatDate(deal.expireDate)}
              </Typography>
            </Grid>

            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                Next Billing
              </Typography>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                {formatDate(deal.nextBillingDate)}
              </Typography>
            </Grid>
          </Grid>
        )}

        {/* XML Feed URL (Company Managed) */}
        {isCompanyManaged && (
          <Stack spacing={2}>
            {feedUrl && (
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 0.5, fontWeight: 600, fontSize: "0.75rem", color: "text.primary" }}>XML Feed URL</FormLabel>
                <TextField
                  size="small"
                  value={feedUrl}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={copied ? "Copied!" : "Copy URL"}>
                          <IconButton onClick={handleCopyFeedUrl} edge="end" size="small">
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "grey.50",
                      "& fieldset": { borderColor: "divider" },
                    },
                    "& input": {
                      color: "text.secondary",
                      fontFamily: "monospace",
                      fontSize: 12,
                    },
                  }}
                />
              </FormControl>
            )}
            <Grid container spacing={2} sx={{ pt: 0.5 }}>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Created At
                </Typography>
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  {formatDate(deal.createdAt)}
                </Typography>
              </Grid>
            </Grid>
          </Stack>
        )}

        {/* Error message */}
        {deal.errorMessage && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              bgcolor: "error.light",
              color: "error.contrastText",
              p: 1,
              borderRadius: 1,
            }}
          >
            <ErrorOutlineIcon fontSize="small" sx={{ color: "error.main" }} />
            <Typography variant="body2" fontWeight={500} sx={{ color: "error.main", fontSize: "0.8rem" }}>
              Error: {deal.errorMessage}
            </Typography>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

export default LinkedinDealItem;
